<template>
  <main class="page-tutor-list">
    <TutorListFilter class="mt-10" />
    <TutorListTeacherList
      class="container mt-8 lg:mt-12"
      @updatedData="updatedData"
    />
    <TutorListCurvePrice v-if="hasPriceChart" class="lg:mt-[120px] mt-16" />
    <TutorListStudentsTestimonial v-if="hasTestimonials" class="page-tutor-list__section" />
    <TutorListFaq v-if="hasFAQS" class="page-tutor-list__section" />
    <div v-if="getSeoContent && getSeoContent.body" class="page-tutor-list__section">
      <SectionSeoContentBox />
    </div>

    <SectionSeoContentInternalLinks v-if="hasInternalLinks" class="mt-7" />
  </main>
</template>

<script>
import { mapGetters } from 'vuex'
import { renderHead } from '@helper/head/head-home'
import { hydrateWhenIdle, hydrateWhenVisible } from 'vue-lazy-hydration'
import { isEmpty } from 'lodash'
import TutorListFilter from '~/components/pages/tutor-list/filter/filter.vue'
import TutorListTeacherList from '~/components/pages/tutor-list/teacher-list/teacher-list'

export default {
  name: 'PageTutorList',
  components: {
    SectionSeoContentInternalLinks: hydrateWhenVisible(
      () => import('@design-system/section/seo-content/seo-content-internal-links'),
      { observerOptions: { rootMargin: '100px' } }
    ),
    SectionSeoContentBox: hydrateWhenVisible(
      () => import('@design-system/section/seo-content/seo-content-box'),
      { observerOptions: { rootMargin: '100px' } }
    ),
    TutorListTeacherList,
    TutorListFilter,
    TutorListCurvePrice: hydrateWhenIdle(() => import('~/components/pages/tutor-list/curve-price/curve-price.vue'),
      { observerOptions: { rootMargin: '100px' } }),
    TutorListStudentsTestimonial: hydrateWhenVisible(
      () => import('~/components/pages/tutor-list/testimonial/testimonial'),
      { observerOptions: { rootMargin: '100px' } }
    ),
    TutorListFaq: hydrateWhenVisible(
      () => import('~/components/pages/tutor-list/tutor-list-faq.vue'),
      { observerOptions: { rootMargin: '100px' } }
    )
  },
  auth: false,
  provide () {
    const pagination = {}
    Object.defineProperty(pagination, 'data', {
      enumerable: true,
      get: () => this.getPagination
    })
    return {
      pagination,
      meta: this.getMeta
    }
  },
  layout: 'main',
  async asyncData ({ params, store, query }) {
    const parameters = JSON.parse(JSON.stringify(params))
    const queries = JSON.parse(JSON.stringify(query))
    if (queries.page_number && isNaN(queries.page_number)) {
      queries.page_number = 1
    }

    if (queries?.page_number) {
      if (parseInt(queries?.page_number) > 1) {
        queries.ignore_skip = true
      }
    }
    await store.dispatch('local/storeListParams', parameters)
    await store.dispatch('local/storeListQueries', queries)
    await store.dispatch('local/asyncDataTutorList')
    await store.dispatch('local/fetchListPriceChart')
  },
  head () {
    const currentPage = this.getPagination?.page_number
    const total = this.getPagination?.total
    const perPage = this.getPagination?.per_page
    const isFirstPage = currentPage === 1
    const isSecondPage = currentPage === 2
    const isLastPage = currentPage >= Math.ceil(total / perPage)
    const canonical = this.getMeta && this.getMeta?.canonical ? this.getMeta.canonical.split('?')[0] : []
    const link = []
    if (!isFirstPage) {
      link.push({
        rel: 'prev',
        href: canonical + (!isSecondPage ? ('?page_number=' + (currentPage - 1)) : '')
      })
    }
    if (!isLastPage) {
      link.push({
        rel: 'next',
        href: canonical + ('?page_number=' + (currentPage + 1))
      })
    }
    return renderHead({ meta: this.getMeta, data: { link } })
  },
  computed: {
    ...mapGetters('global', ['getTestimonials', 'getMeta', 'getPagination', 'getFaqs', 'getInternalLinks', 'getSeoContent']),
    ...mapGetters('local', ['getSchemaRating', 'getListParams', 'getListQueries', 'getPriceChart']),

    hasTestimonials () {
      return !isEmpty(this.getTestimonials)
    },
    hasFAQS () {
      return !isEmpty(this.getFaqs) && !this.$route.query.page_number
    },
    hasPriceChart () {
      return !isEmpty(this.getPriceChart) && !this.$route.query.page_number
    },
    hasInternalLinks () {
      return !isEmpty(this.getInternalLinks)
    }
  },
  watchQuery: true,
  created () {
    this.$nuxt.$on('fetchList', this.updatedData)
  },
  beforeDestroy () {
    this.$nuxt.$off('fetchList')
  },
  methods: {
    updatedData () {
      const url = this.localePath({
        name: this.$routes.tutor_list.name,
        params: this.getListParams,
        query: this.getListQueries
      })
      this.$router.replace(url)
    }
  }
}
</script>

<style lang="scss" scoped>
.page-tutor-list {
  &__section {
    @apply mt-16 lg:mt-[120px];
  }
}
</style>
